import { useMemo } from 'react';
import { Redirect } from 'react-router';
import styled from 'styled-components';

import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { useModalContext } from 'utils/contexts/modal';
import { useToastNotification } from 'utils/hooks/useToastNotification';
import { FACEBOOK_INTEGRATION_URL } from '../../constants';
import { AdAccountType, FacebookModalState } from '../../domain/modal';
import { useSubmitAdAccounts } from '../../hooks/useSubmitAdAccounts';
import { useAdAccountContext } from '../../contexts/AdAccountContext';
import { useFacebookIntegrationContext } from '../../contexts/FacebookIntegrationContext';

const StyledModal = styled(Modal)`
  overflow: visible;
`;

const StyledModalBody = styled(ModalBody)`
  overflow: visible;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const isAdAccountConfirmationModalVisible = (
  modalState: FacebookModalState | null,
): modalState is AdAccountType =>
  !!modalState && 'adAccountIds' in modalState && !!modalState.adAccountIds.length;

const displayContent = {
  header: 'Select Ad Accounts',
  placeholder: 'select ad accounts',
  confirm: 'Confirm',
  modalTextMultiple: 'Are you sure you want to add ad accounts: ',
  modalTextSingle: 'Are you sure you want to add the ad account: ',
};

const constructNegativeMessage = (adAccountIds: string) => (
  <>
    <span>There was an error:</span>
    <div>
      <span>Failed to add </span>
      {adAccountIds.length ? (
        <BoldText>{adAccountIds.length}</BoldText>
      ) : (
        <span>an ad account id</span>
      )}
    </div>
  </>
);

export const AdAccountConfirmationModal = () => {
  const { adAccountIds } = useAdAccountContext();
  const { submit, isSuccess, isError } = useSubmitAdAccounts();
  const { isGettingIntegrations, hasReceivedIntegrations } = useFacebookIntegrationContext();
  const { modalState, hideModal } = useModalContext<FacebookModalState>();
  const modalBodyText =
    adAccountIds.length > 1 ? displayContent.modalTextMultiple : displayContent.modalTextSingle;
  const strAdAccountIds = useMemo(() => adAccountIds.join(','), [adAccountIds]);
  const modalBodyAdAccountText = useMemo(() => adAccountIds.join(', '), [adAccountIds]);
  const canRedirectToFBCA = isSuccess && !isGettingIntegrations && hasReceivedIntegrations;
  const negativeMessage = constructNegativeMessage(strAdAccountIds);

  const handleClose = () => {
    submit(strAdAccountIds);
    hideModal();
  };

  useToastNotification({
    enabled: isError,
    negative: isError,
    negativeMessage,
  });

  if (canRedirectToFBCA) {
    return <Redirect to={FACEBOOK_INTEGRATION_URL} />;
  }

  if (!isAdAccountConfirmationModalVisible(modalState)) return null;

  return (
    <StyledModal
      id="select-ad-account-modal"
      isModalOpen
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{displayContent.header}</ModalHeader>
      <StyledModalBody>
        {modalBodyText}
        <BoldText>{modalBodyAdAccountText}</BoldText>
      </StyledModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleClose}
          isDisabled={!adAccountIds.length}
          size={BUTTON_SIZES.LARGE}
        >
          {displayContent.confirm}
        </PrimaryButton>
      </ModalFooter>
    </StyledModal>
  );
};
